import {
    RowChangeEvent,
    WeekDayFocusEvent,
    WeekdaySelection,
} from '@/modules/location/components/schedule/WeekdaySelection.tsx';
import { WeekdayName, WeekdayNameSchema } from '../../domain/Weekday';
import { EmitterSubscriber } from '@/shared/hooks/useEmitter.ts';
import { TimePicker } from '@schuettflix/react-components';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

export const FormScheduleTimeSchema = z.object({
    weekdays: z.array(WeekdayNameSchema),
    start: z.string().nullable(),
    end: z.string().nullable(),
});
export type FormScheduleTime = z.input<typeof FormScheduleTimeSchema>;

type OpeningTimePickerProps = {
    rowIndex: number;
    value: FormScheduleTime;
    onChange: (value: FormScheduleTime) => void;
    onRowChange: (event: RowChangeEvent) => void;
    focusEmitter?: EmitterSubscriber<WeekDayFocusEvent>;
    startError?: string;
    endError?: string;
};

export const ScheduleTimePicker = (props: OpeningTimePickerProps) => {
    const { t } = useTranslation();

    const handleWeekdayChange = (weekday: WeekdayName) => {
        const { weekdays } = props.value;

        props.onChange({
            ...props.value,
            weekdays: weekdays.includes(weekday) ? weekdays.filter(i => i !== weekday) : [...weekdays, weekday],
        });
    };

    function handleStartChange(newStart: string | null) {
        props.onChange({
            ...props.value,
            start: newStart,
        });
    }

    function handleEndChange(newEnd: string | null) {
        props.onChange({
            ...props.value,
            end: newEnd,
        });
    }

    return (
        <div className="@2xl:flex-row flex flex-col gap-x-4 gap-y-2">
            <div className="flex-1">
                <WeekdaySelection
                    rowIndex={props.rowIndex}
                    selected={props.value.weekdays}
                    onSelectionChange={handleWeekdayChange}
                    onRowChange={props.onRowChange}
                    focusEmitter={props.focusEmitter}
                />
            </div>
            <div className="flex flex-1 gap-x-2">
                <div className="flex-1">
                    <TimePicker
                        buttonClassName="location-configuration-time-from h-[48px] w-full"
                        optionsClassName="z-10"
                        placeholder={t('location.openingTimes.placeholderTimeFrom')}
                        initialTimeToFocus="05:00"
                        value={props.value.start}
                        onChange={handleStartChange}
                        errorMessage={props.startError}
                    />
                </div>
                <div className="flex-1">
                    <TimePicker
                        buttonClassName="location-configuration-time-to h-[48px] w-full"
                        optionsClassName="z-10"
                        placeholder={t('location.openingTimes.placeholderTimeTo')}
                        initialTimeToFocus="16:00"
                        value={props.value.end}
                        onChange={handleEndChange}
                        errorMessage={props.endError}
                    />
                </div>
            </div>
        </div>
    );
};
